import React from 'react';
import Dialog from 'components/Dialog';
import {Grid, Typography} from '@mui/material';
import {useValues} from 'common/hooks/useValues';
import {
   FilterPartnerType,
   FilterTransactions,
   FilterTransactionsNames,
   FilterTransactionType,
   IFilterTransactions,
} from 'types/diplomat/FilterTransactions';
import Formik from 'components/hook-form/Formik';
import FormikTransferTree from 'components/inputs/FormikTransferTree';
import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import FormikTextField from 'components/inputs/FormikTextField';
import Panel from 'components/Panel';
import {layoutSelectors} from 'features/layout/redux';
import {useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import HookFormRadioGroup from 'components/inputs/FormikRadioGroup';
import {ReadOnlyProvider} from 'common/context/ReadOnlyContext';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import HookFormSwitch from 'components/inputs/FormikSwitch';
import {useFilteredTransactionsOrPartners} from 'common/hooks/useFilteredTransactionsOrPartners';

type FilterTransactionDialogProps = {
   open: boolean;
   header: string;
   onClose: (items?: IFilterTransactions) => void;
   initialValues?: IFilterTransactions;
};

type FilterTransactionFormProps = {onClose: () => void};

const FilterTransactionForm = ({onClose}: FilterTransactionFormProps) => {
   const {t} = useTranslation();
   const transactionsFolders = useSelector(layoutSelectors.transactionSyncFoldersSelector);
   const partnersFolders = useSelector(layoutSelectors.partnersFoldersSelector);

   const filter = useValues<IFilterTransactions>(
      FilterTransactionsNames._filter
   ) as IFilterTransactions['_filter'];
   const type = useValues<IFilterTransactions>(
      FilterTransactionsNames._type
   ) as IFilterTransactions['_type'];
   const isByPartners = useValues<IFilterTransactions>(
      FilterTransactionsNames._byPartners
   ) as IFilterTransactions['_byPartners'];
   const filtered = useFilteredTransactionsOrPartners(filter, isByPartners);

   const typeName = isByPartners ? 'Partner' : 'Transaction';

   return (
      <Grid container spacing={3}>
         <Grid item xs={12}>
            <Typography>
               {t(
                  'Choose which {{typeName}}s you wish to include in the generated Connection Map.',
                  {typeName: typeName.toLocaleLowerCase()}
               )}
            </Typography>
         </Grid>
         <Grid item xs={12} sx={{textAlign: 'center'}}>
            <HookFormSwitch
               name={FilterTransactionsNames._byPartners}
               labels={[t('By Transactions'), t('By Partners')]}
            />
         </Grid>
         <Grid item xs={8}>
            <HookFormRadioGroup
               name={FilterTransactionsNames._type}
               options={isByPartners ? FilterPartnerType.options : FilterTransactionType.options}
            />
         </Grid>
         <Grid item xs={4}>
            <FormikCheckbox name={FilterTransactionsNames.activeOnly} label={t('Active Only')} />
         </Grid>
         <ReadOnlyProvider value={type !== FilterTransactionType.enum.SELECTED}>
            <Grid item xs={12}>
               <FormikTransferTree
                  key={
                     isByPartners
                        ? FilterTransactionsNames.partners
                        : FilterTransactionsNames.transactions
                  }
                  name={
                     isByPartners
                        ? FilterTransactionsNames.partners
                        : FilterTransactionsNames.transactions
                  }
                  options={filtered}
                  folders={isByPartners ? partnersFolders : transactionsFolders}
                  leftLabel={t('Available {{typeName}}s', {typeName})}
                  rightLabel={t('Selected {{typeName}}s', {typeName})}
               />
            </Grid>

            <Grid container justifyContent="center">
               <Grid item md={6} sm={8}>
                  <Panel name={t('Filter Criteria')}>
                     <Typography>
                        {t(
                           'Enter a phrase to display only {{typeName}} names containing the filter phrase in the Available {{typeName}}s panel.',
                           {typeName}
                        )}
                     </Typography>
                     <Grid container spacing={2} justifyContent="center">
                        <Grid item xs={10}>
                           <FormikTextField name="_filter" label={t('Filter Phrase')} />
                        </Grid>
                        {/* <Grid item><Button>{t('')}</Button></Grid>
                     <Grid item><Button>{t('')}</Button></Grid> */}
                     </Grid>
                  </Panel>
               </Grid>
            </Grid>
         </ReadOnlyProvider>
         {/* {isGenerating && (
            <Grid container justifyContent="center">
               <Loader />
            </Grid>
         )} */}
         <FormFooterButtons submitLabel={t('Ok')} cancel={onClose} />
      </Grid>
   );
};

const FilterTransactionDialog = ({
   header,
   onClose,
   open,
   initialValues,
}: FilterTransactionDialogProps) => {
   return (
      <Dialog open={open} maxWidth={'md'} header={header} close={onClose}>
         <Formik
            initialValues={
               initialValues ||
               FilterTransactions.cast({
                  [FilterTransactionsNames.transactions]: [],
                  [FilterTransactionsNames.partners]: [],
               })
            }
            initialTouched={[FilterTransactionsNames._filter]}
            onSubmit={(values, helpers) => {
               console.debug('Filter', values);
               onClose(values);
            }}
         >
            <FilterTransactionForm onClose={onClose} />
         </Formik>
      </Dialog>
   );
};

export default React.memo(FilterTransactionDialog);
